import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FormField, Input, Modal, Select } from '@/components/ui';
import { useTouchedFields } from './useTouchedFields';
import {
    EMPTY_PROFILE_FORM,
    GENDER_OPTIONS,
    maskCccdDisplay,
    type ProfileFormValues,
} from './profileFormState';
import { splitVietnameseName } from './nameSplit';
import styles from './ProfileOnboardingGate.module.css';

export interface ProfileOnboardingGateProps {
    open: boolean;
    displayName?: string;
    /** "Để sau" — ALWAYS available, at either step (ticket: never a wall). */
    onSkip: () => void;
    /** Fires after step 2's "Hoàn tất" — see the component doc for why this
     *  cannot actually persist anything yet. */
    onComplete: () => void;
}

function ShieldIcon() {
    return (
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
            <path d="M12 3l7 3v5.5c0 4.3-2.9 7.7-7 9-4.1-1.3-7-4.7-7-9V6l7-3z" strokeLinejoin="round" />
        </svg>
    );
}

/**
 * `/profile` first-login onboarding gate — frame 20 (`20-profile-onboarding.png`).
 *
 * SCOPE NOTE (read before wiring this in): frame 20's export only shows
 * step 1's fields (Họ, Tên, Giới tính required + Ngày sinh, Số điện thoại
 * optional — "asking only Họ, Tên, Giới tính plus two optional fields", per
 * the ticket). Step 2's fields were not captured in the frame; this
 * implementation infers step 2 asks for CMND/CCCD (optional, per the
 * ticket's CMND policy paragraph) because the SAME frame also ships a
 * "CMND/CCCD là dữ liệu định danh" privacy-explainer card, which only makes
 * sense alongside a CMND input. That inference is NOT a captured frame
 * literal — flagged in the EAA-1822 report.
 *
 * PERSISTENCE — there is no portal-api endpoint for any of these fields (see
 * `profileFormState.ts`). "Tiếp tục"/"Hoàn tất" therefore only navigate
 * between the two local steps and validate; `onComplete` is called at the
 * end so the caller can dismiss the gate, but nothing is actually saved
 * anywhere — the caller must not treat completion as "profile now complete"
 * against real data, only "the user finished this local flow".
 *
 * MOUNTING — this component was exported but not mounted anywhere before this
 * change. It is now mounted from `ProfilePage.tsx` (this session's own file),
 * gated by `sessionStorage` (see `ProfilePage.tsx`'s `hasSeenOnboardingGate` /
 * `markOnboardingGateSeen`) rather than a real "is this the account's first
 * login" flag — there is no portal-api profile record to answer that
 * question from (same gap this whole component's SCOPE note describes), so
 * the honest proxy is "has this browser session already dismissed or
 * finished the gate for this sub". That means the gate only ever appears
 * when a user actually visits `/profile` in a session that has not seen it
 * yet, NOT immediately after the real Keycloak login redirect — mounting it
 * at the true post-login moment needs `App.tsx` / `AuthProvider`, which is
 * out of this session's file ownership. The launcher strip below is a
 * SEPARATE surface: it belongs on the launcher/home screen so a skip still
 * escalates somewhere the person will see it outside `/profile` — see the
 * EAA-1822 report for the exact snippet to mount it in `LauncherPage.tsx`.
 */
export function ProfileOnboardingGate({
    open,
    displayName,
    onSkip,
    onComplete,
}: ProfileOnboardingGateProps) {
    const { t } = useTranslation();
    // Seeded from the OIDC `name` claim, same as the edit view (frame 20's
    // mock itself shows "Nguyễn"/"Thu Hà" pre-filled, not blank) — this is
    // real data already on the token at this point, not a fabricated value.
    const seeded = splitVietnameseName(displayName);
    const initialValues: ProfileFormValues = { ...EMPTY_PROFILE_FORM, ho: seeded.ho, ten: seeded.ten };

    const [step, setStep] = useState<1 | 2>(1);
    const [values, setValues] = useState<ProfileFormValues>(initialValues);
    const { touch, markAttempted, shouldShowError, reset } = useTouchedFields();
    // EAA-1822: same display-only CMND/CCCD mask as ProfileEditView — see
    // `maskCccdDisplay`'s doc comment.
    const [cccdFocused, setCccdFocused] = useState(false);

    function set<K extends keyof ProfileFormValues>(key: K, value: ProfileFormValues[K]) {
        setValues((prev) => ({ ...prev, [key]: value }));
    }

    function handleSkip() {
        setStep(1);
        reset();
        setValues(initialValues);
        onSkip();
    }

    function handleContinue() {
        markAttempted();
        if (!values.ho.trim() || !values.ten.trim() || !values.gioiTinh) return;
        reset();
        setStep(2);
    }

    function handleBack() {
        setStep(1);
    }

    function handleFinish() {
        setStep(1);
        reset();
        setValues(initialValues);
        onComplete();
    }

    const hoError = shouldShowError('ho') && !values.ho.trim() ? t('profile.fields.hoRequired') : null;
    const tenError = shouldShowError('ten') && !values.ten.trim() ? t('profile.fields.tenRequired') : null;
    const gioiTinhError =
        shouldShowError('gioiTinh') && !values.gioiTinh ? t('profile.fields.gioiTinhRequired') : null;

    return (
        <Modal
            open={open}
            width={520}
            closable={false}
            maskClosable={false}
            keyboard={false}
            onCancel={handleSkip}
            title={
                <>
                    <div className={styles.progress}>
                        <span className={`${styles.progressSeg} is-done`} />
                        <span className={`${styles.progressSeg}${step === 2 ? ' is-done' : ''}`} />
                        <span className={styles.progressLabel}>
                            {t('profile.onboarding.stepLabel', { step })}
                        </span>
                    </div>
                    <span className={styles.title}>
                        {step === 1
                            ? t('profile.onboarding.step1Title')
                            : t('profile.onboarding.step2Title')}
                    </span>
                    <span className={styles.desc}>
                        {step === 1
                            ? t('profile.onboarding.step1Description')
                            : t('profile.onboarding.step2Description', {
                                  name: displayName ?? t('profile.onboarding.defaultName'),
                              })}
                    </span>
                </>
            }
            footer={
                <div className={styles.footer}>
                    <button type="button" className={styles.skip} onClick={handleSkip}>
                        {t('profile.onboarding.skip')}
                    </button>
                    {/* EAA-1804: kit `Button` instead of hand-written `<button className="portal-btn">`. */}
                    {step === 2 && <Button onClick={handleBack}>{t('profile.onboarding.back')}</Button>}
                    <Button type="primary" onClick={step === 1 ? handleContinue : handleFinish}>
                        {step === 1 ? t('profile.onboarding.continue') : t('profile.onboarding.finish')}
                    </Button>
                </div>
            }
        >
            {/* EAA-1804: kit `FormField`/`Input`/`Select` replacing the app-local
                `./FormField` twin and raw `<input>`/`<select>` elements —
                `helper` renamed to `hint`, invalid state is antd's
                `status="error"` instead of a hand-built `is-invalid` class,
                and `fullWidth` (spanned `.portal-field-grid`, a concept the
                kit doesn't have) becomes `className="portal-field-span-2"`
                passed straight through (that CSS class still exists — other
                folders' hand-rolled field chrome still uses its siblings). */}
            {step === 1 ? (
                <div className="portal-field-grid" style={{ padding: '18px 0 0' }}>
                    <FormField label={t('profile.fields.ho')} required error={hoError}>
                        <Input
                            status={hoError ? 'error' : undefined}
                            value={values.ho}
                            onChange={(e) => set('ho', e.target.value)}
                            onBlur={() => touch('ho')}
                            autoFocus
                        />
                    </FormField>
                    <FormField label={t('profile.fields.ten')} required error={tenError}>
                        <Input
                            status={tenError ? 'error' : undefined}
                            value={values.ten}
                            onChange={(e) => set('ten', e.target.value)}
                            onBlur={() => touch('ten')}
                        />
                    </FormField>
                    <FormField label={t('profile.fields.gioiTinh')} required error={gioiTinhError}>
                        <Select
                            status={gioiTinhError ? 'error' : undefined}
                            placeholder={t('profile.fields.gioiTinhPlaceholder')}
                            value={values.gioiTinh === '' ? undefined : values.gioiTinh}
                            onChange={(value) => set('gioiTinh', value as ProfileFormValues['gioiTinh'])}
                            onBlur={() => touch('gioiTinh')}
                            // EAA-1845: see ProfileEditView.tsx's identical comment — the
                            // rendered label is looked up here by value, not from
                            // GENDER_OPTIONS.label (profileFormState.ts can't call hooks).
                            options={GENDER_OPTIONS.map((opt) => ({
                                value: opt.value,
                                label: t(`profile.genderOptions.${opt.value}`),
                            }))}
                        />
                    </FormField>
                    <FormField label={t('profile.fields.ngaySinh')}>
                        <Input
                            value={values.ngaySinh}
                            onChange={(e) => set('ngaySinh', e.target.value)}
                            placeholder={t('profile.fields.ngaySinhPlaceholder')}
                        />
                    </FormField>
                    <FormField
                        label={t('profile.fields.phone')}
                        className="portal-field-span-2"
                        hint={t('profile.onboarding.phoneHelper')}
                    >
                        <Input
                            value={values.phone}
                            onChange={(e) => set('phone', e.target.value)}
                            placeholder={t('profile.fields.phonePlaceholder')}
                        />
                    </FormField>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', paddingTop: 18 }}>
                    <FormField label={t('profile.fields.cccd')} hint={t('profile.fields.cccdHelper')}>
                        <Input
                            value={cccdFocused ? values.cccd : maskCccdDisplay(values.cccd)}
                            onChange={(e) => set('cccd', e.target.value)}
                            onFocus={() => setCccdFocused(true)}
                            onBlur={() => setCccdFocused(false)}
                            placeholder={t('profile.fields.cccdPlaceholder')}
                            inputMode="numeric"
                            autoComplete="off"
                        />
                    </FormField>
                    <div className={styles.infoCard}>
                        <span className={styles.infoIcon} aria-hidden="true">
                            <ShieldIcon />
                        </span>
                        <div className={styles.infoBody}>
                            <span className={styles.infoTitle}>
                                {t('profile.onboarding.cccdInfoTitle')}
                            </span>
                            <span className={styles.infoDesc}>
                                {t('profile.onboarding.cccdInfoDescription')}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
}

const LAUNCHER_DISMISSED_KEY = 'portal-profile-onboarding-launcher-dismissed';

export interface ProfileOnboardingLauncherStripProps {
    onOpenProfile: () => void;
}

/**
 * The "escalates to a dismissible launcher strip once per session, not a
 * wall" surface (frame 20's "NHẮC TRONG PORTAL" card) — shown instead of the
 * modal after "Để sau", dismissible, and gated to once per browser session
 * via `sessionStorage` (there is no backend "have I nagged this user
 * already" flag to read, so session-scoped is the honest local equivalent).
 *
 * Not mounted by this change — see `ProfileOnboardingGate`'s doc comment.
 */
export function ProfileOnboardingLauncherStrip({ onOpenProfile }: ProfileOnboardingLauncherStripProps) {
    const { t } = useTranslation();
    const [dismissed, setDismissed] = useState<boolean>(() => {
        try {
            return sessionStorage.getItem(LAUNCHER_DISMISSED_KEY) === '1';
        } catch {
            return false;
        }
    });

    if (dismissed) {
        return null;
    }

    function handleDismiss() {
        try {
            sessionStorage.setItem(LAUNCHER_DISMISSED_KEY, '1');
        } catch {
            /* sessionStorage unavailable (private mode) — dismiss for this render only */
        }
        setDismissed(true);
    }

    return (
        <div className={styles.launcher}>
            <span className={styles.launcherLabel}>{t('profile.onboardingLauncher.label')}</span>
            <div className={styles.nudge}>
                <span className={styles.nudgeText}>
                    {t('profile.onboardingLauncher.nudgeText')}
                </span>
                <button type="button" className={styles.nudgeAction} onClick={onOpenProfile}>
                    {t('profile.onboardingLauncher.action')}
                </button>
                <button
                    type="button"
                    className={styles.nudgeAction}
                    onClick={handleDismiss}
                    aria-label={t('profile.onboardingLauncher.dismissAriaLabel')}
                    title={t('common.close')}
                >
                    ×
                </button>
            </div>
            <span className={styles.launcherNote}>
                {t('profile.onboardingLauncher.note')}
            </span>
        </div>
    );
}

export default ProfileOnboardingGate;
